import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { gsApi, gsApiV3 } from "../auth/auth";
import {
  fileDownloadThunkResponse,
  getThunkResponse,
  payouts,
  registrationOrders,
  thunkResponse,
  tournament,
  v3RegistrationCustomers,
} from "../app/api";
import {
  extraLongDelay,
  getIDRelationship,
  getOpts,
  getResponse,
  getSerializedData,
  mediumDelay,
} from "../helpers/JSONapi";

export const getOrders = createAsyncThunk(
  "reporting/getOrders",
  async (tournamentID, thunkAPI) => {
    const response = await gsApi.get(`${tournament}/${tournamentID}/registration-orders`);
    return response;
  }
);
export const getOrder = createAsyncThunk(
  "reporting/getOrder",
  async (orderID, thunkAPI) => {
    const response = await gsApi.get(`${registrationOrders}/${orderID}`);
    return response;
  }
);
export const getRefunds = createAsyncThunk(
  "reporting/getRefunds",
  async (orderID, thunkAPI) => {
    const response = await gsApi.get(
      `${registrationOrders}/${orderID}/registration-order-refunds`
    );
    return response;
  }
);

export const getPayouts = createAsyncThunk(
  "reporting/getPayouts",
  async (tournamentID, thunkAPI) => {
    const response = await gsApi.get(`${tournament}/${tournamentID}/payouts`);
    return response;
  }
);

export const getPayoutRequestor = createAsyncThunk(
  "reporting/getPayoutRequestor",
  async (payoutID, thunkAPI) => {
    const response = await gsApi.get(`${payouts}/${payoutID}/requestor`);
    return response;
  }
);

export const getEventOrderSummary = createAsyncThunk(
  "reporting/getEventOrderSummary",
  async (tournamentID, thunkAPI) => {
    const response = await gsApi.get(
      `${tournament}/${tournamentID}/event-order-summary`
    );
    return response;
  }
);

export const getTournamentHasOpenInvoices = createAsyncThunk(
  "reporting/getTournamentHasOpenInvoices",
  async (tournamentID, thunkAPI) => {
    const response = await gsApi.get(
      `${tournament}/${tournamentID}/has-open-invoices`
    );
    return response?.data?.has_open_invoices;
  }
);

//resend product invoices
export const resendProductInvoices = createAsyncThunk(
  "reporting/resendProductInvoices",
  async (tournamentID, thunkAPI) => {
    return await getResponse(
      gsApi.post,
      `${tournament}/${tournamentID}/resend-open-invoices`,
      {},
      thunkAPI
    );
  }
);

//resend receipt
export const resendReceipt = createAsyncThunk(
  "reporting/resendReceipt",
  async (id, thunkAPI) => {
    return await getResponse(
      gsApi.post,
      `${registrationOrders}/${id}/resend-receipt`,
      {},
      thunkAPI
    );
  }
);

// resend invoice
export const resendInvoice = createAsyncThunk(
  "reporting/resendInvoice",
  async (id, thunkAPI) => {
    return await getResponse(
      gsApi.post,
      `${registrationOrders}/${id}/resend-invoice`,
      {},
      thunkAPI
    );
  }
);

//refund order
export const refundOrder = createAsyncThunk(
  "reporting/refundOrder",
  async (currentOrder, thunkAPI) => {
    return await getResponse(
      gsApi.post,
      `${registrationOrders}/${currentOrder.id}/refund`,
      {
        amount: currentOrder?.purchaseCost - currentOrder?.nonRefundableFees,
      },
      thunkAPI
    );
  }
);

//void order
export const voidOrder = createAsyncThunk(
  "reporting/refundOrder",
  async (orderID, thunkAPI) => {
    return await getResponse(
      gsApi.post,
      `${registrationOrders}/${orderID}/refund`,
      {},
      thunkAPI
    );
  }
);

// void invoice
export const voidInvoice = createAsyncThunk(
  "reporting/voidInvoice",
  async (id, thunkAPI) => {
    return await getResponse(
      gsApi.post,
      `${registrationOrders}/${id}/void-invoice`,
      {},
      thunkAPI
    );
  }
);

export const downloadCustomerInvoice = createAsyncThunk(
  "reporting/downloadCustomerInvoice",
  async (id, thunkAPI) => {
    return await gsApi.get(`${registrationOrders}/${id}/download-customer-invoice`, {
      responseType: "arraybuffer"
    });
  }
);

export const downloadRegistrationOrderReceipt = createAsyncThunk(
  "reporting/downloadRegistrationOrderReceipt",
  async (id, thunkAPI) => {
    return await gsApi.get(`${registrationOrders}/${id}/download-registration-order-receipt`, {
      responseType: "arraybuffer"
    });
  }
);

// mark invoice as paid
export const markInvoiceAsPaid = createAsyncThunk(
  "reporting/markInvoiceAsPaid",
  async (id, thunkAPI) => {
    return await getResponse(
      gsApi.post,
      `${registrationOrders}/${id}/mark-invoice-as-paid`,
      {},
      thunkAPI
    );
  }
);

// mark invoice as unpaid
export const markInvoiceAsUnpaid = createAsyncThunk(
  "reporting/markInvoiceAsUnpaid",
  async (id, thunkAPI) => {
    return await getResponse(
      gsApi.post,
      `${registrationOrders}/${id}/mark-invoice-as-unpaid`,
      {},
      thunkAPI
    );
  }
);

//request payout
export const requestTournamentPayout = createAsyncThunk(
  "reporting/requestTournamentPayout",
  async (payout, thunkAPI) => {
    const serialiedData = getSerializedData(
      payout,
      "payouts",
      getOpts(payout, [...getIDRelationship("tournament")])
    );
    return await getResponse(gsApi.post, `${payouts}`, serialiedData, thunkAPI);
  }
);

//save registration customer
export const saveRegistrationCustomer = createAsyncThunk(
  "reporting/saveRegistrationCustomer",
  async (customer, thunkAPI) => {
    const payload = {
      firstName: customer?.firstName ?? "",
      lastName: customer?.lastName ?? "",
      email: customer?.email ?? "",
      phone: customer?.phone ?? "",
      companyName: customer?.companyName ?? "",
    };
    return await getResponse(
      gsApiV3.patch,
      `${v3RegistrationCustomers}/${customer.id}`,
      payload,
      thunkAPI
    );
  }
);

//replace all occurences of reporting with the name of your new slice
export const reportingSlice = createSlice({
  name: "reporting",
  initialState: {
    orders: [],
    orderFilter: [],
    currentOrder: {},
    currentOrderRefunds: [],
    payouts: [],
    currentPayout: {},
    orderSummary: {},
    notifications: [],
    loading: [],
    hasOpenInvoices: false,
  },
  //be sure to export any reducers on line 65
  reducers: {
    setOrders: (state, action) => {
      state.orders = action.payload;
    },
    addNotification: (state, action) => {
      state.notifications.push(action.payload);
    },
    clearNotifications: (state, action) => {
      state.notifications = [];
    },
    updatePayout: (state, action) => {
      state.currentPayout = action.payload;
    },
    setOrderFilter: (state, action) => {
      state.orderFilter = action.payload;
    },
    updateCurrentOrderCustomer: (state, action) => {
      state.currentOrder = {
        ...state.currentOrder,
        registrationCustomer: action.payload,
      };
    },
  },
  extraReducers: {
    ...getThunkResponse(getOrders, (state, action) => {
      state.orders = action.payload;
    }),
    ...getThunkResponse(getOrder, (state, action) => {
      state.currentOrder = action.payload;
      // keep the list in sync with the freshly-fetched order so edits made on
      // the detail/edit screens are reflected when navigating back to the list
      state.orders = state.orders?.map?.((order) =>
        order?.id === action.payload?.id
          ? { ...order, registrationCustomer: action.payload?.registrationCustomer }
          : order
      );
    }),
    ...getThunkResponse(getRefunds, (state, action) => {
      state.currentOrderRefunds = action.payload;
    }),
    ...getThunkResponse(getPayouts, (state, action) => {
      state.payouts = action.payload;
    }),
    ...getThunkResponse(getEventOrderSummary, (state, action) => {
      state.orderSummary = action.payload;
    }),
    ...getThunkResponse(getTournamentHasOpenInvoices, (state, action) => {
      state.hasOpenInvoices = action.payload;
    }),
    ...fileDownloadThunkResponse(downloadCustomerInvoice, (state, action) => {
      return `${state.currentOrder.tournament.name.replaceAll(' ', '-')}-invoice.pdf`;
    }),
    ...fileDownloadThunkResponse(downloadRegistrationOrderReceipt, (state, action) => {
      return `${state.currentOrder.tournament.name.replaceAll(' ', '-')}-receipt.pdf`;
    }),
    ...thunkResponse(
      resendProductInvoices,
      (state, action) => {},
      "Resending Product Invoices",
      {
        pendingDelay: mediumDelay,
        fulfilledDelay: mediumDelay,
        rejectedDelay: extraLongDelay,
        pendingText: "Sending Product Invoices",
        fulfilledText: "All GolfStatus product invoices have been resent.",
      }
    ),
    ...thunkResponse(
      resendReceipt,
      (state, action) => {},
      "Resending Receipt",
      {
        pendingDelay: mediumDelay,
        fulfilledDelay: mediumDelay,
        rejectedDelay: extraLongDelay,
        pendingText: "Sending Receipt",
        fulfilledText: "Receipt Sent",
      }
    ),
    ...thunkResponse(
      resendInvoice,
      (state, action) => {},
      "Resending Invoice",
      {
        pendingDelay: mediumDelay,
        fulfilledDelay: mediumDelay,
        rejectedDelay: extraLongDelay,
        pendingText: "Sending Invoice",
        fulfilledText: "Invoice Sent",
      }
    ),
    ...thunkResponse(refundOrder, (state, action) => {}, "Refund Order", {
      pendingDelay: mediumDelay,
      fulfilledDelay: mediumDelay,
      rejectedDelay: extraLongDelay,
      pendingText: "Refunding Order",
      fulfilledText: "Order Refunded",
    }),
    ...thunkResponse(voidInvoice, (state, action) => {}, "Void Invoice", {
      pendingDelay: mediumDelay,
      fulfilledDelay: mediumDelay,
      rejectedDelay: extraLongDelay,
      pendingText: "Voiding Invoice",
      fulfilledText: "Invoice Voided",
    }),
    ...thunkResponse(
      markInvoiceAsPaid,
      (state, action) => {
        state.currentOrder = {...state?.currentOrder, ...action.payload};
        const index = state.orders.findIndex(o => o.id === action.payload.id);
        if (index !== -1) {
          let updatedOrders = [...state.orders];
          // original order contains additional associations we don't want to overwrite
          updatedOrders[index] = {...updatedOrders[index], ...action.payload};
          state.orders = updatedOrders;
        }
      },
      "Mark Invoice as Paid",
      {
        pendingDelay: mediumDelay,
        fulfilledDelay: mediumDelay,
        rejectedDelay: extraLongDelay,
        pendingText: "Marking Invoice as Paid",
        fulfilledText: "Invoice Marked as Paid",
      }
    ),
    ...thunkResponse(
      markInvoiceAsUnpaid,
      (state, action) => {
        state.currentOrder = {...state?.currentOrder, ...action.payload};
        const index = state.orders.findIndex(o => o.id === action.payload.id);
        if (index !== -1) {
          let updatedOrders = [...state.orders];
          // original order contains additional associations we don't want to overwrite
          updatedOrders[index] = {...updatedOrders[index], ...action.payload};
          state.orders = updatedOrders;
        }
      },
      "Mark Invoice as Unpaid",
      {
        pendingDelay: mediumDelay,
        fulfilledDelay: mediumDelay,
        rejectedDelay: extraLongDelay,
        pendingText: "Marking Invoice as Unpaid",
        fulfilledText: "Invoice Marked as Unpaid",
      }
    ),
    ...thunkResponse(
      saveRegistrationCustomer,
      (state, action) => {
        state.currentOrder = {
          ...state.currentOrder,
          registrationCustomer: {
            ...state.currentOrder?.registrationCustomer,
            ...action.payload,
          },
        };
      },
      "Save Customer",
      {
        pendingDelay: mediumDelay,
        fulfilledDelay: mediumDelay,
        rejectedDelay: extraLongDelay,
        pendingText: "Saving Customer",
        fulfilledText: "Customer Updated",
      }
    ),
    ...thunkResponse(
      requestTournamentPayout,
      (state, action) => {},
      "Request Tournament Payout",
      {
        pendingDelay: mediumDelay,
        fulfilledDelay: mediumDelay,
        rejectedDelay: extraLongDelay,
        pendingText: "Requesting Payout",
        fulfilledText: "Payout complete!",
      }
    ),
  },
});

export const {
  setOrders,
  updatePayout,
  addNotification,
  clearNotifications,
  setOrderFilter,
  updateCurrentOrderCustomer,
} = reportingSlice.actions;

//export any selectors needed
export const selectOrders = (state) => state?.[reportingSlice.name]?.orders;
export const selectCurrentOrder = (state) =>
  state?.[reportingSlice.name]?.currentOrder;
export const selectCurrentOrderRefunds = (state) =>
  state?.[reportingSlice.name]?.currentOrderRefunds;
export const selectPayouts = (state) => state?.[reportingSlice.name]?.payouts;
export const selectCurrentPayout = (state) =>
  state?.[reportingSlice.name]?.currentPayout;
export const selectOrderSummary = (state) =>
  state?.[reportingSlice.name]?.orderSummary;
export const selectHasOpenInvoices = (state) =>
  state?.[reportingSlice.name]?.hasOpenInvoices

export const selectNotifications = (state) =>
  state?.[reportingSlice.name]?.notifications;

export const selectLoading = (state) => state?.[reportingSlice.name]?.loading;

export const selectOrderFilter = (state) => state?.[reportingSlice.name]?.orderFilter

export default reportingSlice.reducer;

//ADD reducer to store.js
